import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { usePageMeta, useArtists } from "@/hooks";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";

// Roster figures as of July 2026 — sourced from the production database.
const STATS = [
  { value: "2020", label: "Operating since" },
  { value: "16", label: "Artists on the roster" },
  { value: "250+", label: "Portfolio pieces" },
  { value: "5.0", label: "Average review rating" },
];

const VALUES = [
  {
    title: "100% Human-Made",
    description:
      "Every commission starts as a rough sketch you approve and ends as high-resolution files from a real artist's hands.",
    proof: "Our anti-AI policy isn't marketing — it's a term in our TOS.",
  },
  {
    title: "Artist-First",
    description:
      "Each artist sets their own prices, deadlines, and will/won't-draw list. We handle quotes, invoices, and translation so they can spend their time drawing.",
    proof: "Every artist page lists their own prices and terms.",
  },
  {
    title: "Everyone Welcome",
    description:
      "LGBTQ+ friendly, furry friendly, OC and fandom friendly. Sixteen artists with wildly different styles means someone here will love drawing your character.",
    proof: "From chibi to painterly — browse the roster and see.",
  },
];

function PortfolioMarquee() {
  const { data: artists } = useArtists();

  const images = useMemo(() => {
    // Two newest safe images per artist, artists ordered by most recent upload,
    // so the strip shows fresh work while still rotating through the roster.
    const perArtist = (artists ?? [])
      .map((artist) =>
        (artist.portfolio ?? [])
          .filter((img) => img.contentRating === "safe")
          .sort(
            (a, b) =>
              new Date(b.uploadedAt || 0).getTime() -
              new Date(a.uploadedAt || 0).getTime()
          )
          .slice(0, 2)
      )
      .filter((list) => list.length > 0)
      .sort(
        (a, b) =>
          new Date(b[0].uploadedAt || 0).getTime() -
          new Date(a[0].uploadedAt || 0).getTime()
      );
    const firstRound = perArtist.flatMap((list) => (list[0] ? [list[0]] : []));
    const secondRound = perArtist.flatMap((list) => (list[1] ? [list[1]] : []));
    return [...firstRound, ...secondRound].slice(0, 18);
  }, [artists]);

  if (images.length < 6) return null;

  return (
    <section className="py-16 overflow-hidden">
      <div className="relative">
        <div
          className="flex w-max gap-4 animate-marquee"
          style={{ animationDuration: "70s" }}
        >
          {[...images, ...images].map((img, i) => (
            <img
              key={`${img.id}-${i}`}
              src={img.thumbnail || img.url}
              alt={img.altText || "Commissioned artwork from a StudioComm artist"}
              loading="lazy"
              decoding="async"
              className="h-36 md:h-44 w-auto rounded-xl border border-[var(--color-border)] object-cover"
            />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[var(--color-bg)] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[var(--color-bg)] to-transparent" />
      </div>
      <p className="text-center text-sm text-[var(--color-text-muted)] mt-6">
        Real commissions from our artists —{" "}
        <a
          href="/#portfolio"
          className="text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors"
        >
          browse the full gallery
        </a>
      </p>
    </section>
  );
}

export function AboutPage() {
  usePageMeta({
    title: "About StudioComm — 100% Human-Made Art Since 2020",
    description:
      "StudioComm is an independent art commission studio running since 2020 — 16 artists, 250+ finished pieces, and a 5.0 client rating. Every piece hand-drawn by real human artists. No AI, ever.",
    canonicalPath: "/about",
  });
  return (
    <div className="bg-[var(--color-bg)]">
      {/* Hero Banner — rendered at full aspect ratio, nothing overlaid on the art */}
      <section className="relative">
        <img
          src="/static/images/241204_brazillian_miku_komiko_3.webp"
          alt="Komiko and friends at the beach — artwork by Aobari"
          className="w-full h-auto"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[var(--color-bg)]" />
        <Link
          to="/artist/aobari"
          className="absolute bottom-3 right-3 md:bottom-6 md:right-6 z-10 text-xs px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-colors"
        >
          Artwork by Aobari
        </Link>
      </section>

      {/* Page Title */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-[var(--font-display)] text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)]"
          >
            About <em className="text-[var(--color-primary)]">StudioComm</em>
          </motion.h1>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-b border-[var(--color-border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map(({ value, label }, index) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="font-[var(--font-display)] text-4xl md:text-5xl text-[var(--color-primary)] mb-2">
                {value}
              </div>
              <div className="text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)]">
                {label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Manifesto + Values */}
      <section className="py-20 bg-[var(--color-bg-card)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-[var(--font-display)] text-4xl md:text-6xl text-[var(--color-text)] leading-tight mb-6">
              We Stand for <em className="text-[var(--color-primary)]">Artists</em>
            </h2>
            <p className="text-[var(--color-text-muted)] text-lg max-w-2xl mx-auto">
              It's written into our{" "}
              <Link
                to="/how-it-works#tos"
                className="text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors"
              >
                Terms of Service
              </Link>
              : no AI-generated art, no NFTs, ever. You approve a hand-made sketch before
              any color goes down, and your finished piece arrives from a named artist you
              can follow, credit, and commission again.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map(({ title, description, proof }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                className="bg-[var(--color-bg)] rounded-xl p-6 border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors flex flex-col"
              >
                <h3 className="text-xl font-semibold text-[var(--color-text)] mb-3">{title}</h3>
                <p className="text-[var(--color-text-muted)] mb-4">{description}</p>
                <p className="text-sm text-[var(--color-primary)] mt-auto">{proof}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Marquee */}
      <PortfolioMarquee />

      {/* Meet Komiko */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-primary)]/5 rounded-2xl p-8 md:p-12 border border-[var(--color-primary)]/30">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="order-2 md:order-1"
              >
                <span className="text-xs font-medium uppercase tracking-widest text-[var(--color-primary)] mb-2 block">
                  Our Mascot
                </span>
                <h2 className="text-3xl font-bold text-[var(--color-text)] mb-4">
                  Meet <em className="text-[var(--color-primary)]">Komiko</em>
                </h2>
                <p className="text-[var(--color-text-muted)] mb-4">
                  Komiko is our fox-spirit mascot — equal parts guide and quality
                  inspector. She stars in the comic that walks you through the commission
                  process, step by step.
                </p>
                <p className="text-[var(--color-text-muted)] mb-6">
                  Like everything else on this site, she's hand-drawn: her chibi forms are
                  by <Link to="/artist/evih" className="text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors">Evih</Link>,
                  and the beach scene at the top of this page is by{" "}
                  <Link to="/artist/aobari" className="text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors">Aobari</Link> —
                  both artists you can commission right now.
                </p>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center gap-2 text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors font-medium"
                >
                  Read Komiko's comic <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="order-1 md:order-2 flex justify-center"
              >
                <motion.img
                  src="/static/images/decorations/chibis/image01.png"
                  alt="Komiko chibi with her puppy — art by Evih"
                  className="max-w-full h-auto max-h-64 object-contain drop-shadow-xl"
                  animate={{ y: [0, -15, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Reviews */}
      <ReviewsCarousel />

      {/* CTA */}
      <section className="py-20 bg-[var(--color-bg-card)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] mb-4">
              Let's Draw <em className="text-[var(--color-primary)]">Your Character</em>
            </h2>
            <p className="text-[var(--color-text-muted)] mb-8 max-w-2xl mx-auto text-lg">
              Pick an artist whose style you love and tell us about your character. Our
              team will get back to you on your request within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#artists"
                className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] transition-colors shadow-lg hover:shadow-xl"
              >
                Browse Artists
              </a>
              <a
                href="/#contact"
                className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[var(--color-bg)] border-2 border-[var(--color-border)] text-[var(--color-text)] font-medium hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
              >
                Start a Commission
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
